import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAppSelector } from './hooks/useAppDispatch';
import { Layout } from './components/Layout';
import { AdminLoginRoute, LegacyLoginRedirect } from './components/AuthRouteGuards';
import { ADMIN_LOGIN_PATH, USER_HOME_PATH, hasCentralAdminClaims } from './utils/adminAccess';
import { OverviewPage } from './pages/Overview';
import { TokensPage } from './pages/Tokens';
import { AttemptsPage } from './pages/Attempts';
import { AppsStatusPage } from './pages/AppsStatus';
import { BillingPage } from './pages/Billing';
import { AuthLoginPage } from './pages/AuthLogin';
import { AuthSignupPage } from './pages/AuthSignup';
import { WorkspacePickerPage } from './pages/WorkspacePicker';
import { SignedInLandingPage } from './pages/SignedInLanding';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ResetPasswordPage } from './pages/ResetPassword';
import { ChangePasswordPage, CHANGE_PASSWORD_PATH } from './pages/ChangePassword';
import { VerifyEmailPage } from './pages/VerifyEmail';
import { ConfirmEmailChangePage } from './pages/ConfirmEmailChange';
import { UserEmailChangePage } from './pages/UserEmailChange';
import {
  UsersPage,
  UserDetailPage,
  WorkspacesPage,
  WorkspaceDetailPage,
} from './pages/directory';
import { AcceptInvitePage } from './pages/AcceptInvite';
import { StartEntryPage } from './pages/StartEntry';
import { WorkspaceHomePage } from './pages/WorkspaceHome';
import {
  BusinessSetupPage,
  PersonalSetupPage,
  OutcomeSelectionPage,
  PricingSummaryPage,
  ReviewSuggestionsPage,
  InviteTeammatesPage,
  CompletePage,
  NewWorkspacePage,
  OnboardingCompatRedirect,
  OnboardingWelcomeGate,
} from './pages/onboarding';
import { WorkspaceSettingsPage } from './pages/settings/WorkspaceSettings';
import { BillingReturnPage } from './pages/BillingReturn';
import { SIGNED_IN_LANDING_PATH } from './utils/signInLanding';

function ProtectedRoutes() {
  const { isAuthenticated, token } = useAppSelector((state) => state.auth);

  // The admin console is for tokens `requireAdmin` would accept. Everyone
  // else — no token, or an ordinary user's workspace token from the settings
  // session bridge — belongs in the user area, never on the JWT-paste page.
  // `/home` sends a session-less visitor on to Central sign-in itself.
  // Admins arrive here from Central sign-in (utils/signInLanding.ts stores
  // their bridged token first) or explicitly via ADMIN_LOGIN_PATH.
  if (!isAuthenticated || !hasCentralAdminClaims(token)) {
    return <Navigate to={USER_HOME_PATH} replace />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<OverviewPage />} />
        <Route path="/tokens" element={<TokensPage />} />
        <Route path="/attempts" element={<AttemptsPage />} />
        <Route path="/apps" element={<AppsStatusPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/users/email-change" element={<UserEmailChangePage />} />
        {/* Read-only directory. `/users/email-change` above is a static
            segment, so it still outranks `/users/:userId`. */}
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/:userId" element={<UserDetailPage />} />
        <Route path="/workspaces" element={<WorkspacesPage />} />
        <Route path="/workspaces/:workspaceId" element={<WorkspaceDetailPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#333',
            color: '#fff',
            borderRadius: '8px',
          },
          success: {
            iconTheme: {
              primary: '#25BD71',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <Routes>
        {/* OIDC Auth Flow Routes (public) */}
        <Route path="/auth/login" element={<AuthLoginPage />} />
        <Route path="/auth/signup" element={<AuthSignupPage />} />
        <Route path="/auth/workspace" element={<WorkspacePickerPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        {/* Replacing an admin-issued temporary password (session cookie). */}
        <Route path={CHANGE_PASSWORD_PATH} element={<ChangePasswordPage />} />
        <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/confirm-email-change" element={<ConfirmEmailChangePage />} />
        <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />
        {/* Post-sign-in landing with no continue target: Central admins go on
            to the console, everyone else to /home (utils/signInLanding.ts). */}
        <Route path={SIGNED_IN_LANDING_PATH} element={<SignedInLandingPage />} />

        {/* Entry point routes (business / personal) */}
        <Route path="/start/:mode" element={<StartEntryPage />} />

        {/* Post-onboarding workspace landing (session-authed page) */}
        <Route path="/home" element={<WorkspaceHomePage />} />

        {/* Onboarding entry / workspace chooser. A brand-new account first
            sees the Central Welcome (server-decided), then this chooser. */}
        <Route
          path="/onboarding/new"
          element={
            <OnboardingWelcomeGate>
              <NewWorkspacePage />
            </OnboardingWelcomeGate>
          }
        />

        {/* R7: mode-specific onboarding route trees */}
        <Route path="/onboarding/business/setup" element={<BusinessSetupPage />} />
        <Route path="/onboarding/business/outcomes" element={<OutcomeSelectionPage />} />
        <Route path="/onboarding/business/pricing" element={<PricingSummaryPage />} />
        <Route path="/onboarding/business/review" element={<ReviewSuggestionsPage />} />
        <Route path="/onboarding/business/invites" element={<InviteTeammatesPage />} />
        <Route path="/onboarding/business/complete" element={<CompletePage />} />
        <Route path="/onboarding/personal/setup" element={<PersonalSetupPage />} />
        <Route path="/onboarding/personal/outcomes" element={<OutcomeSelectionPage />} />
        <Route path="/onboarding/personal/pricing" element={<PricingSummaryPage />} />
        <Route path="/onboarding/personal/complete" element={<CompletePage />} />

        {/* R7: legacy flat-route compatibility — redirect to the
            mode-specific route, preserving any ?continue= param. */}
        <Route path="/onboarding/setup" element={<OnboardingCompatRedirect fixedMode="business" step="setup" />} />
        <Route path="/onboarding/personal-setup" element={<OnboardingCompatRedirect fixedMode="personal" step="personal-setup" />} />
        <Route path="/onboarding/purpose" element={<OnboardingCompatRedirect fixedMode="business" step="setup" />} />
        <Route path="/onboarding/website" element={<OnboardingCompatRedirect fixedMode="business" step="setup" />} />
        <Route path="/onboarding/review" element={<OnboardingCompatRedirect fixedMode="business" step="review" />} />
        <Route path="/onboarding/invites" element={<OnboardingCompatRedirect fixedMode="business" step="invites" />} />
        <Route path="/onboarding/outcomes" element={<OnboardingCompatRedirect step="outcomes" />} />
        <Route path="/onboarding/pricing" element={<OnboardingCompatRedirect step="pricing" />} />
        <Route path="/onboarding/complete" element={<OnboardingCompatRedirect step="complete" />} />
        <Route path="/onboarding/type" element={<OnboardingCompatRedirect step="setup" />} />

        {/* Workspace Settings (workspace-scoped routes) */}
        <Route path="/workspace/:workspaceId/settings" element={<WorkspaceSettingsPage />} />
        <Route path="/workspace/:workspaceId/settings/:tab" element={<WorkspaceSettingsPage />} />
        <Route path="/workspace/:workspaceId/settings/:tab/:action" element={<WorkspaceSettingsPage />} />

        {/* Billing: Stripe Checkout lands here, then on to a validated return_to */}
        <Route path="/billing/return" element={<BillingReturnPage />} />

        {/* Settings routes — all handled by WorkspaceSettingsPage with session bridge */}
        <Route path="/settings" element={<WorkspaceSettingsPage />} />
        <Route path="/settings/:tab" element={<WorkspaceSettingsPage />} />
        <Route path="/settings/:tab/:action" element={<WorkspaceSettingsPage />} />

        {/* Admin Login — explicit admin-only route. `/login` is the legacy
            name and now means "sign in": it forwards to /auth/login. */}
        <Route path={ADMIN_LOGIN_PATH} element={<AdminLoginRoute />} />
        <Route path="/login" element={<LegacyLoginRedirect />} />
        <Route path="/*" element={<ProtectedRoutes />} />
      </Routes>
    </BrowserRouter>
  );
}
